import { Accordion, AccordionDetails, AccordionSummary, Divider, Button } from '@mui/material';
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { HiOutlineChevronDown } from 'react-icons/hi';
import { FiClock } from 'react-icons/fi';
import { Bold, Title } from '../styles';
import pixLogo from '../../../assets/pix.svg';

// eslint-disable-next-line react/prop-types
const PixCodeBox = ({ children }) => (
  <div style={{
    border: "1px dashed #D2D2D7",
    borderRadius: "8px",
    padding: "12px 14px",
    fontFamily: "monospace",
    fontSize: "11px",
    lineHeight: "1.6",
    wordBreak: "break-all",
    color: "#333",
    marginBottom: "12px",
  }}>
    {children}
  </div>
);

export default function ProductHeader({ showDeliveryAddress, paymentData, shippingAddress, totalOrder, status, discountPoints, couponCode }) {
  const [copied, setCopied] = useState(false);

  const expirationTime = useMemo(() => {
    if (!paymentData?.pixData?.pixExpiresAt) return "--:--";
    return new Date(paymentData.pixData.pixExpiresAt).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [paymentData?.pixData?.pixExpiresAt]);

  const handleCopyCode = async () => {
    const code = paymentData?.pixData?.pixCode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  return (
    <>
    <Title style={{ margin: "24px 0 20px" }}>
      {status || '-'}
    </Title>
    <Accordion sx={{ boxShadow: 0 }} className="accordion" defaultExpanded={!!paymentData?.pixData}>
      <AccordionSummary
        className="accordion-summary p-0"
        expandIcon={<HiOutlineChevronDown />}
        aria-controls="panel1a-content"
      >
        <Title style={{ fontSize: "15px", fontWeight: "500" }}>
          Forma de Pagamento
        </Title>
      </AccordionSummary>
      <AccordionDetails>
        <Title style={{ fontSize: "15px", fontWeight: "500", marginBottom: "8px", opacity: "0.6" }}>
          {paymentData?.type || '-'}
        </Title>
        <div style={{ maxWidth: "100%", wordBreak: "break-word" }}>
          <Bold style={{ fontSize: "13px", fontWeight: "300", marginBottom: "12px", marginTop: "0px" }}>
            {paymentData?.description || '-'}
            {paymentData?.cardInfo ? (
              <>
                <br />
                {paymentData.cardInfo}
              </>
            ) : null}
          </Bold>
        </div>

        {(discountPoints > 0 || couponCode) && (
          <div style={{ marginTop: "18px" }}>
            <Title style={{ fontSize: "15px", fontWeight: "500", marginBottom: "8px", opacity: "0.6" }}>
              Cupom de desconto
            </Title>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Bold style={{ fontSize: "13px", fontWeight: "300" }}>
                {discountPoints > 0 && (
                  <span style={{ color: "var(--primary-color)" }}>
                    -{Number(discountPoints).toLocaleString("pt-BR")} pts
                  </span>  
                )}
                {couponCode && (
                  <span style={{ color: "var(--primary-color)", marginLeft: "5px" }}>
                    ({couponCode})
                  </span>  
                )}
              </Bold>
            </div>
          </div>
        )}

        {paymentData?.pixData && (
          <div style={{ marginTop: "16px" }}>
            <div style={{ textAlign: "center", margin: "16px 0" }}>
              <img src={pixLogo} alt="Pix" style={{ width: 28, height: 28, marginBottom: 6 }} />
              <p className="font-wl-600 text-sm" style={{ marginBottom: 0, color: "var(--primary-color)" }}>
                Pagamento via Pix
              </p>
            </div>
            <p className="font-wl-400 text-xs" style={{ textAlign: "center", color: "#767F8D", marginBottom: "12px" }}>
              Copie e cole o código, no aplicativo do seu banco, ou escaneie o
              código QR com a câmera do seu celular.
            </p>

            <PixCodeBox>{paymentData.pixData.pixCode}</PixCodeBox>

            <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", color: "#2D8D68", marginBottom: "12px" }}>
              <FiClock size={14} />
              <span className="text-xs font-wl-500">
                O código é válido até às {expirationTime}.
              </span>
            </div>

            <button
              type="button"
              className="btn btn-default-wl text-xxs font-wl-700 w-100"
              onClick={handleCopyCode}
              style={{ marginBottom: "16px" }}
            >
              {copied ? "CÓDIGO COPIADO COM SUCESSO" : "COPIAR CÓDIGO PIX"}
            </button>

            {paymentData.pixData.pixQrCode && (
              <div style={{ textAlign: "center", margin: "16px 0" }}>
                <img
                  src={paymentData.pixData.pixQrCode}
                  alt="QR Code Pix"
                  style={{ width: 160, height: 160 }}
                />
              </div>
            )}
          </div>
        )}
      </AccordionDetails>
    </Accordion>
    {showDeliveryAddress && shippingAddress && (
      <Accordion sx={{ boxShadow: 0 }} className="accordion">
        <AccordionSummary
          className="accordion-summary p-0"
          expandIcon={<HiOutlineChevronDown />}
          aria-controls="panel2a-content"
        >
          <Title style={{ fontSize: "15px", fontWeight: "500" }}>
            Endereço de entrega
          </Title>
        </AccordionSummary>
        <AccordionDetails>
          <Title style={{ fontSize: "15px", fontWeight: "500", marginBottom: "16px", opacity: "0.6" }}>
            {shippingAddress.label || '-'}
          </Title>
          <div style={{ maxWidth: "100%", wordBreak: "break-word" }}>
            <Bold style={{ fontSize: "12px", marginBottom: "12px", marginTop: "0px", fontWeight: "300" }}>
              {shippingAddress.street || '-'}
              {shippingAddress.neighborhood ? (
                <>
                  <br />
                  {shippingAddress.neighborhood}
                  {shippingAddress.complement ? `, ${shippingAddress.complement}` : ''}
                </>
              ) : null}
              {(shippingAddress.city || shippingAddress.state || shippingAddress.zipCode) ? (
                <>
                  <br />
                  {shippingAddress.city ? `${shippingAddress.city}` : '-'}
                  {shippingAddress.state ? `, ${shippingAddress.state}` : ''}
                  {shippingAddress.zipCode ? ` - ${shippingAddress.zipCode}` : ''}
                </>
              ) : null}
            </Bold>
          </div>
        </AccordionDetails>
      </Accordion>
    )}
    <Divider />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0 10px" }}>
      <Title>
        Total do pedido
      </Title>
      <Title>
        {totalOrder || '-'}
      </Title>
    </div>
    <Divider />
    </>
  );
}

ProductHeader.propTypes = {
  showDeliveryAddress: PropTypes.bool,
  paymentData: PropTypes.shape({
    type: PropTypes.string,
    description: PropTypes.string,
    cardInfo: PropTypes.string,
    pixData: PropTypes.shape({
      pixCode: PropTypes.string,
      pixQrCode: PropTypes.string,
      pixExpiresAt: PropTypes.string,
    }),
  }),
  shippingAddress: PropTypes.shape({
    label: PropTypes.string,
    street: PropTypes.string,
    neighborhood: PropTypes.string,
    complement: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
  }),
  totalOrder: PropTypes.string,
  status: PropTypes.string,
  discountPoints: PropTypes.number,
  couponCode: PropTypes.string,
};

ProductHeader.defaultProps = {
  showDeliveryAddress: false,
  paymentData: null,
  shippingAddress: null,
  totalOrder: '0,00 pts',
  status: 'Aprovado',
};
